import { yupResolver } from '@hookform/resolvers/yup';
import { FC, useEffect } from 'react';
import { useForm } from 'react-hook-form';

import { newsWireSchema } from '@constants';
import { queryClient } from '@helpers';

import QueryKeys from '@constants/query-keys';

import MainModal from './MainModal';
import { TableEnterpriseNewsWire } from '../constants';
import { useMutation } from 'react-query';
import { ApiClientEnterprise } from '@helpers/apiClients';
import { successMessage } from '@services';
import { useParams } from 'react-router-dom';

interface AddNewsWireModalProps {
  onClose: () => void;
  open: boolean;
  title: string;
  subtitle: string;
  editingItem?: TableEnterpriseNewsWire;
}

export const AddNewsWireModal: FC<AddNewsWireModalProps> = ({
  onClose,
  open,
  title,
  subtitle,
  editingItem,
}) => {
  const {
    handleSubmit,
    control,
    reset,
    formState: { isValid },
  } = useForm<TableEnterpriseNewsWire>({
    resolver: yupResolver(newsWireSchema),
    defaultValues: editingItem,
    mode: 'onChange',
    delayError: 700,
  });

  const isEdit = !!editingItem;
  const { id: organizationId } = useParams<{ id: string }>();

  useEffect(() => {
    if (isEdit && editingItem) {
      reset(editingItem);
    } else {
      reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingItem, reset]);

  const { mutate: createNewsWire, isLoading: isCreating } = useMutation({
    mutationFn: (data: TableEnterpriseNewsWire) =>
      ApiClientEnterprise.post(`/enterprises-admin/${organizationId}/newswire`, {
        ...data,
        publishedAt: new Date().toISOString(),
      }),

    onSuccess: () => {
      successMessage('News wire created successfully!');
      queryClient.refetchQueries(QueryKeys.enterpriseNewsWire);
      handleCloseAndReset();
    },
    onError: (error) => {
      console.error('Error creating news wire:', error);
      successMessage('Failed to create news wire.');
    },
  });

  const { mutate: editNewsWire, isLoading: isEditing } = useMutation({
    mutationFn: (data: TableEnterpriseNewsWire) =>
      ApiClientEnterprise.put(`/enterprises-admin/${organizationId}/newswire/${data.id}`, data),

    onSuccess: () => {
      successMessage('News wire updated successfully!');
      queryClient.refetchQueries(QueryKeys.enterpriseNewsWire);
      handleCloseAndReset();
    },
    onError: (error) => {
      console.error('Error updating news wire:', error);
      successMessage('Failed to update news wire.');
    },
  });

  const handleCloseAndReset = () => {
    reset();
    onClose();
  };

  const onFinalSubmit = (data: TableEnterpriseNewsWire) => {
    if (data) {
      isEdit ? editNewsWire(data) : createNewsWire(data);
    }
  };

  return (
    <MainModal
      isEdit={isEdit}
      open={open}
      isValid={isValid}
      isLoading={isCreating || isEditing}
      control={control}
      handleSubmit={handleSubmit}
      onClose={handleCloseAndReset}
      onFinalSubmit={onFinalSubmit}
      title={title}
      subtitle={subtitle}
    />
  );
};
